import React, { useState, useEffect } from 'react';
import { X, Download, Share } from 'lucide-react';

const LOGO = 'https://customer-assets.emergentagent.com/job_lamnso-hub/artifacts/l5pp8qre_PHOTO-2025-11-06-12-27-43.jpg';
const DISMISS_KEY = 'lamfon_install_banner_dismissed';

/**
 * Smart Install Banner — suggests installing LámFon as an app.
 * Shows on mobile web when the app is not installed.
 * Handles: iOS (Add to Home Screen), Android (Install prompt), and desktop.
 */
export default function InstallBanner() {
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed/standalone
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    setIsStandalone(standalone);

    if (standalone) return;

    // Check dismiss
    const dismissed = sessionStorage.getItem(DISMISS_KEY);
    if (dismissed) return;

    // Detect iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    // Listen for Android/Chrome install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Show on iOS after a short delay
    if (ios) {
      setTimeout(() => setShow(true), 2000);
    }

    // Show on desktop/other after delay if no install prompt fires
    const fallbackTimer = setTimeout(() => {
      if (!standalone) setShow(true);
    }, 5000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      clearTimeout(fallbackTimer);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setShow(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShow(false);
    sessionStorage.setItem(DISMISS_KEY, 'true');
  };

  if (!show || isStandalone) return null;

  return (
    <div className="fixed bottom-16 md:bottom-4 left-3 right-3 z-50 max-w-lg mx-auto animate-slide-up" data-testid="install-banner">
      <div className="bg-white rounded-2xl border border-[#E7E5E4] shadow-xl p-4">
        <button onClick={handleDismiss} className="absolute top-3 right-3 text-[#A8A29E] hover:text-[#57534E]" data-testid="dismiss-install">
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-3">
          <img src={LOGO} alt="LámFon" className="w-12 h-12 rounded-xl flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#1C1917]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Get the LámFon App
            </p>
            <p className="text-[10px] text-[#57534E]">
              {isIOS ? 'Add to Home Screen for the best experience' : 'Install for faster access and offline support'}
            </p>
          </div>
          {deferredPrompt ? (
            <button onClick={handleInstall}
              className="flex items-center gap-1.5 bg-[#4CAF50] hover:bg-[#3D8B40] text-white px-4 py-2 rounded-xl text-xs font-semibold flex-shrink-0 transition-colors"
              data-testid="install-button">
              <Download className="w-3.5 h-3.5" /> Install
            </button>
          ) : isIOS ? (
            <div className="flex items-center gap-1 text-[10px] text-[#4CAF50] font-semibold flex-shrink-0">
              <Share className="w-3.5 h-3.5" /> Tap Share then "Add to Home Screen"
            </div>
          ) : (
            <button onClick={handleDismiss}
              className="text-xs text-[#4CAF50] font-semibold flex-shrink-0">
              Maybe later
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
